import { useEffect, useRef, useState } from 'react';

import { SubmenuNavRef } from '@components/Nav/SubmenuNav';
import { isElementVisible } from '@components/UserOnboardingTooltip/utils';

export interface UseNavAnchorStepOptions {
  cleanup?: (ref: SubmenuNavRef | null) => void;
}

export interface NavAnchorStepResult {
  anchor: HTMLElement | null;
  navEl: HTMLElement | null;
}

const useNavAnchorStep = (
  enabled: boolean,
  getNavRef: () => SubmenuNavRef | null,
  getAnchor: (ref: SubmenuNavRef) => HTMLElement | null,
  options?: UseNavAnchorStepOptions
): NavAnchorStepResult => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [navEl, setNavEl] = useState<HTMLElement | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!enabled) {
      optionsRef.current?.cleanup?.(null);
      setAnchor(null);
      setNavEl(null);
      return;
    }

    let lastRef: SubmenuNavRef | null = null;

    const trySync = () => {
      const ref = getNavRef();
      if (!ref) return false;

      const nav = ref.getNavElement();
      const anchorEl = getAnchor(ref);
      if (!nav || !anchorEl || !isElementVisible(anchorEl)) return false;

      lastRef = ref;
      setAnchor(anchorEl);
      setNavEl(nav);
      return true;
    };

    if (!trySync()) {
      const MAX_ATTEMPTS = 100;
      let attempts = 0;
      const id = setInterval(() => {
        attempts += 1;
        if (trySync() || attempts >= MAX_ATTEMPTS) clearInterval(id);
      }, 50);
      return () => {
        clearInterval(id);
        optionsRef.current?.cleanup?.(lastRef ?? null);
        setAnchor(null);
        setNavEl(null);
      };
    }

    return () => {
      optionsRef.current?.cleanup?.(lastRef ?? null);
      setAnchor(null);
      setNavEl(null);
    };
  }, [enabled, getNavRef, getAnchor]);

  return { anchor, navEl };
};

export default useNavAnchorStep;
